(() => {
  if (customElements.get('korvia-media-stories')) return;
  class KorviaMediaStories extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.kms-track');
      this.cards = Array.from(this.querySelectorAll('.kms-card'));
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = false;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseVideos();
        this.schedule();
      }, { signal });
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.pauseVideos();
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this.track);
      if (this.cards.length < 2) {
        this.track.classList.add('is-static');
        return;
      }
      this.playing = this.dataset.autoplay === 'true' && !this.motion.matches;
      this.querySelector('.kms-controls').hidden = false;
      this.querySelector('.kms-total').textContent = String(this.cards.length).padStart(2, '0');
      this.querySelector('.kms-prev').addEventListener('click', () => this.move(-1, true), { signal });
      this.querySelector('.kms-next').addEventListener('click', () => this.move(1, true), { signal });
      this.toggle = this.querySelector('.kms-toggle');
      this.toggle.addEventListener('click', () => {
        this.playing = !this.playing;
        this.syncToggle();
        this.schedule();
      }, { signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.pause();
        if (event.key === 'Home' || event.key === 'End') this.go(event.key === 'Home' ? 0 : this.cards.length - 1);
        else this.move(event.key === 'ArrowRight' ? 1 : -1, false);
      }, { signal });
      this.track.addEventListener('pointerdown', () => this.pause(), { signal, passive: true });
      this.track.addEventListener('wheel', () => this.pause(), { signal, passive: true });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.updateCount());
      }, { signal, passive: true });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', (event) => {
        this.focused = !this.toggle.contains(event.target);
        this.schedule();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget) && !this.toggle.contains(event.relatedTarget);
        this.schedule();
      }, { signal });
      this.querySelectorAll('video').forEach((video) => video.addEventListener('play', () => this.pause(), { signal }));
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.pause(); }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.findIndex((card) => card.dataset.blockId === event.detail.blockId);
        if (index >= 0) { this.pause(); this.go(index); }
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => {
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        this.querySelector('.kms-controls').hidden = !scrollable;
        this.track.classList.toggle('is-static', !scrollable);
        this.schedule();
      });
      this.resizeObserver.observe(this.track);
      this.syncToggle();
      this.updateCount();
    }
    pauseVideos() { this.querySelectorAll('video').forEach((video) => video.pause()); }
    pause() { this.playing = false; this.syncToggle(); this.schedule(); }
    syncToggle() {
      this.toggle.setAttribute('aria-label', this.playing ? 'Pausar carrusel' : 'Reproducir carrusel');
      this.toggle.setAttribute('aria-pressed', String(this.playing));
      this.toggle.querySelector('span').textContent = this.playing ? 'Pausar' : 'Reproducir';
      this.toggle.querySelector('svg').innerHTML = this.playing ? '<path d="M5 4h3v12H5zM12 4h3v12h-3z"/>' : '<path d="M7 4 15 10 7 16Z"/>';
    }
    currentIndex() {
      const left = this.track.getBoundingClientRect().left;
      return this.cards.reduce((best, card, index) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.cards[best].getBoundingClientRect().left - left) ? index : best, 0);
    }
    updateCount() { this.querySelector('.kms-current').textContent = String(this.currentIndex() + 1).padStart(2, '0'); }
    go(index) {
      this.pauseVideos();
      const target = this.cards[index];
      const offset = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left: offset, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    move(direction, manual) {
      if (manual) this.pause();
      const atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 3;
      const atStart = this.track.scrollLeft < 3;
      if (direction > 0 && atEnd) this.go(0);
      else if (direction < 0 && atStart) this.go(this.cards.length - 1);
      else this.go(Math.max(0, Math.min(this.cards.length - 1, this.currentIndex() + direction)));
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.playing || !this.visible || this.hovered || this.focused || document.hidden || this.motion.matches) return;
      if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
      this.timer = setTimeout(() => { this.move(1, false); this.schedule(); }, Number(this.dataset.duration) || 8000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.raf);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  }
  customElements.define('korvia-media-stories', KorviaMediaStories);
})();
